import { PlusIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import AddTracesToTableDialog from "./AddTracesToTableDialog";
import { useTracesSelectionStore } from "./traces-selection-context";

import { buildTracesSearchParams } from "@/components/TracesSearch/buildTracesSearchParams";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import {
  GridSelectionLimitControl,
  GridSelectionSelectAllMatching,
  HistoryImportActionButton,
  submitHistoryImport,
  useGridSelectionActionBarLimit,
  type HistoryImportConfig,
} from "@/components/ui/ag-grid-table";
import { Button } from "@/components/ui/button";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { ToastType } from "@/enums";
import { EntityType } from "@/queries/folder-entities/types";
import type { SmartTableTraceImportPayload } from "@/queries";
import { displayToast } from "@/utils/toast";

export type TraceImportConfig = HistoryImportConfig<SmartTableTraceImportPayload>;

interface SelectionActionBarProps {
  totalMatching: number | null;
  traceImportConfig?: TraceImportConfig;
}

const SelectionActionBar = observer(
  ({ totalMatching, traceImportConfig }: SelectionActionBarProps) => {
    const selection = useTracesSelectionStore();
    const structuredSearchStore = useStructuredSearchStore();
    const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
    const selectedCount = selection.count;
    const hasSelection = selectedCount > 0;
    const {
      selectAllLimitInput,
      isAllMatching,
      handleLimitInputChange,
      handleSelectAllMatching,
      handleLimitInputBlur,
    } = useGridSelectionActionBarLimit({ selection, totalMatching });

    const showSelectAllMatching =
      traceImportConfig?.selectionLimit !== 1 &&
      selection.mode === "selected" &&
      totalMatching !== null &&
      totalMatching > selection.selectedCount;

    const buildTraceImportPayload = (): SmartTableTraceImportPayload | null => {
      if (selection.mode === "all_matching") {
        const searchParams = buildTracesSearchParams(structuredSearchStore);
        return {
          all_matching: true,
          filter_group: searchParams.filterGroup ?? null,
          ...(selection.matchingLimit ? { limit: selection.matchingLimit } : {}),
          ...(searchParams.sortField ? { sort_field: searchParams.sortField } : {}),
          ...(searchParams.sortOrder ? { sort_order: searchParams.sortOrder } : {}),
        };
      }

      if (selection.selectedIds.length === 0) {
        return null;
      }

      return {
        trace_ids: selection.selectedIds,
      };
    };

    const handleOpenTableDialog = () => {
      const payload = buildTraceImportPayload();
      if (!payload) {
        displayToast("Could not build trace import payload", ToastType.error);
        return;
      }
      setIsTableDialogOpen(true);
    };

    const handleTraceImport = () => {
      if (!traceImportConfig) {
        return;
      }
      void submitHistoryImport({
        config: traceImportConfig,
        selection,
        entityLabel: "trace",
        buildPayload: buildTraceImportPayload,
      });
    };

    if (!hasSelection) {
      return null;
    }

    const summary = `${selectedCount.toLocaleString()} selected`;
    const showStandaloneLimit = traceImportConfig?.selectionLimit !== 1 && isAllMatching;

    return (
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-900">{summary}</span>
        <Button variant="ghost" size="sm" onClick={() => selection.clear()}>
          Clear
        </Button>
        {showSelectAllMatching && totalMatching !== null && (
          <GridSelectionSelectAllMatching
            totalMatching={totalMatching}
            limitInput={selectAllLimitInput}
            onSelectAllMatching={handleSelectAllMatching}
            onLimitChange={handleLimitInputChange}
            onLimitBlur={handleLimitInputBlur}
          />
        )}
        {showStandaloneLimit && (
          <GridSelectionLimitControl
            totalMatching={totalMatching}
            value={selectAllLimitInput}
            showSelectAllMatching={false}
            onChange={handleLimitInputChange}
            onBlur={handleLimitInputBlur}
          />
        )}
        {traceImportConfig ? (
          <HistoryImportActionButton
            config={traceImportConfig}
            selectedCount={selectedCount}
            onClick={handleTraceImport}
          />
        ) : (
          <>
            <Button size="sm" onClick={handleOpenTableDialog}>
              <PlusIcon className="mr-1.5 h-4 w-4" />
              <EntityIcon type={EntityType.SMART_TABLE} size="xs" className="mr-1.5 text-white" />
              Add to Table
            </Button>
            <AddTracesToTableDialog
              isOpen={isTableDialogOpen}
              setOpen={setIsTableDialogOpen}
              buildPayload={buildTraceImportPayload}
              onEnqueueSuccess={() => selection.clear()}
            />
          </>
        )}
      </div>
    );
  },
);

export default SelectionActionBar;
